'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { RenderOneBlock, ThemeContext } from '@akademi/site-builder-runtime';
import { THEME_ID } from '../brand';
import { SpatialKitchenFooter } from '../blocks/SpatialKitchenFooter';
import { SkStyles } from '../styles/SkStyles';
import { footerPropsFromShell, headerPropsFromShell } from './shellMapping';
import { SpatialKitchenHeader } from './SpatialKitchenHeader';

/**
 * Theme override of the `SiteShell` block: this theme's header and footer
 * around the host's own shell.
 *
 * <h3>Why an override, and how it delegates</h3>
 * The storefront renders its chrome as a `SiteShell` block. That host block is
 * the only thing that can reach the route's page content (through a React
 * context private to the storefront), and it renders the runtime's
 * `SiteShellPresenter`, which owns `<main>`. Today the storefront passes no
 * themed header resolver to the presenter, so a theme's `header`/`footer`
 * functions are never called there.
 *
 * <p>So this component renders:
 * <ol>
 *   <li>{@link SpatialKitchenHeader},</li>
 *   <li>the HOST'S `SiteShell` block, reached by rendering `SiteShell` again
 *       under the `default` theme id (the resolver then falls through to the
 *       host's canonical block), with the generic header switched off
 *       (`style: 'none'`) and the generic footer gated off, and</li>
 *   <li>{@link SpatialKitchenFooter}.</li>
 * </ol>
 * The page body, `<main>`, the brand context, the scroll-to-top button and the
 * help-desk widget all still come from the host. Nothing in commerce is
 * forked or modified.
 *
 * <p>The generic footer has no "off" style, so it is hidden with its own
 * audience gate: a security group id no user can hold. This is the generic
 * "Visible to" mechanism, not a DOM hack, and it is applied here in code
 * rather than stored in the seed, so the operator's footer config stays clean.
 *
 * <p>The operator keeps the choice: the kitchen header and footer replace the
 * generic ones only while the shell's `header.style` / `footer.style` is
 * `spatial-kitchen` (or unset). Pick any other style in the Shell Editor and
 * that part renders through the host exactly as before.
 *
 * <p>A guard context stops the delegation from recursing if this bundle is
 * ever registered as the `default` theme itself.
 */
const NEVER_MATCHING_GROUP = -1;

const DelegatingContext = createContext(false);

const SHELL_CSS = `
.sk-shell{display:flex;flex-direction:column;min-height:100vh;background:var(--color-surface-page,#F7F3ED)}
.sk-shell__body{flex:1;display:flex;flex-direction:column}
/* The host shell sets min-height:100vh inline on its wrapper; inside this
   frame the header and footer share the viewport, so let it size to content. */
.sk-shell__body > div:first-of-type{min-height:0 !important;flex:1}
`;

export function SpatialKitchenSiteShell(props: Record<string, unknown>): ReactNode {
  const delegating = useContext(DelegatingContext);
  if (delegating) return null;

  const header = (props.header ?? {}) as Record<string, unknown>;
  const footer = (props.footer ?? {}) as Record<string, unknown>;
  const kitchenHeader = header.style === undefined || header.style === THEME_ID;
  const kitchenFooter = footer.style === undefined || footer.style === THEME_ID;
  const hostShellProps: Record<string, unknown> = {
    ...props,
    header: kitchenHeader ? { ...header, style: 'none', overlay: false } : header,
    footer: kitchenFooter ? { ...footer, requireSecurityGroupIds: [NEVER_MATCHING_GROUP] } : footer,
  };

  return (
    <div className="sk-shell">
      <SkStyles id="shell" css={SHELL_CSS} />
      {kitchenHeader && <SpatialKitchenHeader {...headerPropsFromShell(props)} />}
      <div className="sk-shell__body">
        <DelegatingContext.Provider value={true}>
          <ThemeContext.Provider value={null}>
            <RenderOneBlock instance={{ type: 'SiteShell', props: hostShellProps }} />
          </ThemeContext.Provider>
        </DelegatingContext.Provider>
      </div>
      {kitchenFooter && <SpatialKitchenFooter {...footerPropsFromShell(props)} />}
    </div>
  );
}
